import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { registrationAPI, userAPI } from '../utils/api';
import { toast } from 'react-toastify';
import './Profile.css';

const Profile = () => {
  const { user } = useAuth();
  const [userRegistrations, setUserRegistrations] = useState([]);
  const [profileData, setProfileData] = useState({
    firstName: '',
    lastName: '',
    username: '',
    email: '',
    phone: '',
    address: ''
  });
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) {
      loadUserData();
      loadUserRegistrations();
    }
  }, [user]);

  const loadUserData = async () => {
    try {
      const response = await userAPI.getProfile();
      if (response.success && response.user) {
        const userData = response.user;
        setProfileData({
          firstName: userData.first_name || '',
          lastName: userData.last_name || '',
          username: userData.username || '',
          email: userData.email || '',
          phone: userData.phone || '',
          address: userData.address || ''
        });
      }
    } catch (error) {
      console.error('Fehler beim Laden der Profildaten:', error);
      // Use existing user data as fallback
      setProfileData({
        firstName: user.name ? user.name.split(' ')[0] : '',
        lastName: user.name ? user.name.split(' ')[1] : '',
        username: user.username || '',
        email: user.email || '',
        phone: '',
        address: ''
      });
    }
  };

  const loadUserRegistrations = async () => {
    try {
      const response = await registrationAPI.getUserRegistrations();
      if (response.success) {
        // Gruppiere Registrierungen nach Spiel
        const groupedByGame = response.registrations.reduce((groups, reg) => {
          const key = reg.game_id;
          if (!groups[key]) {
            groups[key] = [];
          }
          groups[key].push(reg);
          return groups;
        }, {});

        // Transformiere zu einer Registrierung pro Spiel mit Teilnehmer-Info
        const transformedRegistrations = Object.values(groupedByGame).map(gameRegs => {
          const firstReg = gameRegs[0];
          const participants = gameRegs.map(reg => ({
            name: `${reg.first_name} ${reg.last_name}`,
            ticketType: reg.ticket_type,
            status: reg.status
          }));
          
          return {
            id: firstReg.id,
            gameId: firstReg.game_id,
            opponent: firstReg.opponent,
            date: firstReg.game_date,
            time: firstReg.game_time,
            registeredAt: firstReg.created_at ? firstReg.created_at.split(' ')[0] : '',
            participants: participants,
            isGroup: participants.length > 1,
            // Status: confirmed wenn alle confirmed, pending wenn mindestens eine pending, cancelled wenn alle cancelled
            status: participants.every(p => p.status === 'confirmed') ? 'confirmed' :
                   participants.some(p => p.status === 'pending') ? 'pending' : 'cancelled'
          };
        });
        
        setUserRegistrations(transformedRegistrations);
      }
    } catch (error) {
      console.error('Fehler beim Laden der Anmeldungen:', error);
      setUserRegistrations([]);
    }
  };

  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setProfileData({
      ...profileData,
      [name]: value
    });
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData({
      ...passwordData,
      [name]: value
    });
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await userAPI.updateProfile(profileData);
      if (response.success) {
        toast.success('Profil wurde erfolgreich aktualisiert!');
      }
    } catch (error) {
      console.error('Fehler beim Aktualisieren des Profils:', error);
      toast.error(error.message || 'Fehler beim Aktualisieren des Profils');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('Die neuen Passwörter stimmen nicht überein!');
      return;
    }

    if (passwordData.newPassword.length < 6) {
      toast.error('Das neue Passwort muss mindestens 6 Zeichen lang sein!');
      return;
    }

    setLoading(true);

    try {
      const response = await userAPI.changePassword({
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword
      });

      if (response.success) {
        toast.success('Passwort wurde erfolgreich geändert!');
        setPasswordData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
      }
    } catch (error) {
      console.error('Fehler beim Ändern des Passworts:', error);
      toast.error(error.message || 'Fehler beim Ändern des Passworts');
    } finally {
      setLoading(false);
    }
  };

  const cancelRegistration = async (registrationId) => {
    const registration = userRegistrations.find(reg => reg.id === registrationId);
    if (!registration) return;

    if (window.confirm('Möchten Sie diese Anmeldung wirklich stornieren?')) {
      try {
        const response = await registrationAPI.cancelRegistration(registration.gameId);
        if (response.success) {
          setUserRegistrations(userRegistrations.filter(reg => reg.id !== registrationId));
          toast.success('Anmeldung wurde storniert!');
        }
      } catch (error) {
        console.error('Fehler beim Stornieren der Anmeldung:', error);
        toast.error(error.message || 'Fehler beim Stornieren der Anmeldung');
      }
    }
  };

  const getTicketTypeLabel = (type) => {
    const types = {
      'stehplatz': 'Stehplatz',
      'sitzplatz': 'Sitzplatz',
      'ohne-ticket': 'Ohne Ticket'
    };
    return types[type] || type;
  };

  const getStatusLabel = (status) => {
    const statuses = {
      'confirmed': 'Bestätigt',
      'pending': 'Ausstehend',
      'cancelled': 'Storniert'
    };
    return statuses[status] || status;
  };

  if (!user) {
    return null;
  }

  return (
    <div className="profile">
      <div className="container">
        <div className="profile-header">
          <h1>Mein Profil</h1>
          <p>Verwalten Sie Ihre Kontoinformationen und Anmeldungen</p>
        </div>

        <div className="profile-content">
          {/* Profile Settings */}
          <div className="profile-section card">
            <h2>Persönliche Daten</h2>
            <form onSubmit={handleProfileSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="firstName">Vorname</label>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    value={profileData.firstName}
                    onChange={handleProfileChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="lastName">Nachname</label>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    value={profileData.lastName}
                    onChange={handleProfileChange}
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="username">Benutzername</label>
                <input
                  type="text"
                  id="username"
                  name="username"
                  value={profileData.username}
                  onChange={handleProfileChange}
                  required
                  pattern="[a-zA-Z0-9_-]+"
                  title="Nur Buchstaben, Zahlen, _ und - erlaubt"
                />
              </div>

              <div className="form-group">
                <label htmlFor="email">E-Mail-Adresse</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={profileData.email}
                  onChange={handleProfileChange}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="phone">Telefonnummer</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={profileData.phone}
                  onChange={handleProfileChange}
                />
              </div>

              <div className="form-group">
                <label htmlFor="address">Adresse</label>
                <input
                  type="text"
                  id="address"
                  name="address"
                  value={profileData.address}
                  onChange={handleProfileChange}
                />
              </div>

              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? 'Speichern...' : 'Profil speichern'}
              </button>
            </form>
          </div>

          {/* Password Change */}
          <div className="profile-section card">
            <h2>Passwort ändern</h2>
            <form onSubmit={handlePasswordSubmit}>
              <div className="form-group">
                <label htmlFor="currentPassword">Aktuelles Passwort</label>
                <input
                  type="password"
                  id="currentPassword"
                  name="currentPassword"
                  value={passwordData.currentPassword}
                  onChange={handlePasswordChange}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="newPassword">Neues Passwort</label>
                <input
                  type="password"
                  id="newPassword"
                  name="newPassword"
                  value={passwordData.newPassword}
                  onChange={handlePasswordChange}
                  required
                  minLength="6"
                />
              </div>

              <div className="form-group">
                <label htmlFor="confirmPassword">Neues Passwort bestätigen</label>
                <input
                  type="password"
                  id="confirmPassword"
                  name="confirmPassword"
                  value={passwordData.confirmPassword}
                  onChange={handlePasswordChange}
                  required
                  minLength="6"
                />
              </div>

              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? 'Ändern...' : 'Passwort ändern'}
              </button>
            </form>
          </div>

          {/* User Registrations */}
          <div className="profile-section card">
            <h2>Meine Anmeldungen</h2>
            {userRegistrations.length === 0 ? (
              <p className="no-registrations">Sie haben sich noch zu keinem Spiel angemeldet.</p>
            ) : (
              <div className="registrations-list">
                {userRegistrations.map(registration => (
                  <div key={registration.id} className="registration-item">
                    <div className="registration-info">
                      <h3>{registration.opponent}</h3>
                      <div className="registration-details">
                        <span><strong>Datum:</strong> {new Date(registration.date).toLocaleDateString('de-DE')} - {registration.time}</span>
                        <span><strong>Angemeldet am:</strong> {new Date(registration.registeredAt).toLocaleDateString('de-DE')}</span>
                        <span className={`status ${registration.status}`}>
                          <strong>Status:</strong> {getStatusLabel(registration.status)}
                        </span>
                        
                        {registration.isGroup ? (
                          <div className="participants-info">
                            <strong>Teilnehmer ({registration.participants.length}):</strong>
                            <div className="participants-list">
                              {registration.participants.map((participant, index) => (
                                <div key={index} className="participant-item">
                                  <span className="participant-name">{participant.name}</span>
                                  <span className="participant-ticket">{getTicketTypeLabel(participant.ticketType)}</span>
                                  <span className={`participant-status ${participant.status}`}>
                                    {getStatusLabel(participant.status)}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        ) : (
                          <span><strong>Ticket:</strong> {getTicketTypeLabel(registration.participants[0].ticketType)}</span>
                        )}
                      </div>
                    </div>

                    <div className="registration-actions">
                      <button
                        onClick={() => window.location.href = `/spiel/${registration.gameId}`}
                        className="btn btn-primary"
                      >
                        Details
                      </button>
                      {registration.status === 'confirmed' && (
                        <button
                          onClick={() => cancelRegistration(registration.id)}
                          className="btn btn-danger"
                        >
                          Stornieren
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Account Info */}
          <div className="profile-section card">
            <h2>Kontoinformationen</h2>
            <div className="account-info">
              <div className="info-item">
                <strong>Mitgliedschaft:</strong> {user.role === 'admin' ? 'Administrator' : 'Mitglied'}
              </div>
              <div className="info-group">
                <strong>Status:</strong> {
                  user.role === 'admin' ? 'Administrator' :
                  user.efc_number ? 'Freigeschaltet' : 'Wartend auf Freischaltung'
                }
              </div>
              {user.efc_number && (
                <div className="info-group">
                  <strong>EFC-Mitgliedsnummer:</strong> {user.efc_number}
                </div>
              )}
              <div className="info-item">
                <strong>E-Mail verifiziert:</strong> {user.emailVerified ? 'Ja' : 'Nein'}
              </div>
              <div className="info-item">
                <strong>Mitglied seit:</strong> August 2025
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;
